// ts-rest contract for the Desk Discretionary Investment Mandate letter.
//
// The client-facing investment mandate ("we will invest X% in equities…")
// produced once per client (and again on a risk-profile change), matching the
// firm's "Discretionary Investment Mandate" document 1:1. The IM enters the
// mandate-specific inputs (asset allocation, fee, initial investment, purpose,
// etc.); the client identity / classification / risk profile `context` is
// assembled server-side from the Client + the latest completed Internal
// Suitability Assessment. The web composes the react-pdf render fields from
// `context` + the stored `inputs`.
//
// Endpoints: `get` (the client's current mandate or a prefilled blank), `save`
// (upsert draft), `issue` (draft → issued; presence tracked for the new-account
// workflow).

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { staffActionErrorSchema } from './agent';

const c = initContract();

import { errorBody } from '../schemas/common';

const statusSchema = z.enum(['draft', 'issued']);
const incomeDispositionSchema = z.enum(['reinvested', 'paid_out']);

// The mandate-specific inputs the IM fills (and that get stored). Every field is
// nullable — a draft is partial. Prose fields carry the bespoke narrative the
// IM tailors per client.
export const mandateLetterInputsSchema = z.object({
    ourRef: z.string().nullable(),
    clientAddress: z.string().nullable(), // multi-line postal address
    // Personal information & background — bespoke prose paragraph.
    backgroundNote: z.string().nullable(),
    // Marital status / dependants comment (the two bracketed inserts).
    maritalDependantsNote: z.string().nullable(),
    // Additional income sources (free text).
    additionalIncomeSources: z.string().nullable(),
    initialInvestmentAmount: z.number().nullable(),
    sourceOfFunds: z.string().nullable(),
    incomeDisposition: incomeDispositionSchema.nullable(),
    specificInvestmentPurpose: z.string().nullable(),
    investmentObjective: z.string().nullable(),
    timeHorizonYears: z.number().nullable(),
    // Strategic asset allocation (percentages).
    allocEquitiesPct: z.number().nullable(),
    allocFixedIncomePct: z.number().nullable(),
    allocPropertyPct: z.number().nullable(),
    allocAlternativesPct: z.number().nullable(),
    allocCashPct: z.number().nullable(),
    strategyExplanation: z.string().nullable(),
    // The bespoke rationale for the recommended risk profile.
    riskRationale: z.string().nullable(),
    // Implications of the risk category for the investment strategy (prose).
    riskImplications: z.string().nullable(),
    annualFeePct: z.number().nullable(),
    // Fee narrative: how the annual fee is calculated, and the additional charges.
    feeCalculationMethod: z.string().nullable(),
    transactionCosts: z.string().nullable(),
    thirdPartyFees: z.string().nullable(),
    // Illiquid-holdings disclosure (the optional paragraph). Null → omit it.
    illiquidPct: z.number().nullable(),
});
export type MandateLetterInputs = z.infer<typeof mandateLetterInputsSchema>;

// Server-derived client context (from Client + latest Suitability Assessment).
export const mandateLetterContextSchema = z.object({
    clientName: z.string(),
    accountNumber: z.string().nullable(),
    county: z.string().nullable(),
    age: z.number().nullable(),
    classification: z.string().nullable(), // e.g. "Retail Client"
    imName: z.string().nullable(),
    taxResidency: z.string().nullable(),
    // Financial background derived from the latest Internal Suitability
    // Assessment (the IM's signed-off numbers), so the letter need not re-key it.
    employmentStatus: z.string().nullable(), // e.g. "Employed"
    annualIncome: z.number().nullable(),
    propertyAssets: z.number().nullable(),
    existingInvestments: z.number().nullable(),
    currentLiabilities: z.number().nullable(),
    monthlyDisposableIncome: z.number().nullable(),
    // From the latest completed Internal Suitability Assessment.
    riskProfileLabel: z.string().nullable(), // e.g. "Moderate Risk"
    benchmarkLabel: z.string().nullable(), // PIMFA benchmark family
    riskDescription: z.string().nullable(), // canonical objective wording
    serviceLabel: z.string().nullable(), // Discretionary / Advisory / Advisory Managed
});
export type MandateLetterContext = z.infer<typeof mandateLetterContextSchema>;

export const mandateLetterDtoSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    imUserId: z.string(),
    status: statusSchema,
    inputs: mandateLetterInputsSchema,
    createdAt: z.string(),
    issuedAt: z.string().nullable(),
});
export type MandateLetterDto = z.infer<typeof mandateLetterDtoSchema>;

// GET response: current mandate (`exists`) or a prefilled blank, plus the
// server-derived `context`. `imSignatory` is the signing IM's display name.
export const mandateLetterGetResponseSchema = z.object({
    clientId: z.string(),
    clientName: z.string(),
    exists: z.boolean(),
    mandate: mandateLetterDtoSchema.nullable(),
    prefillInputs: mandateLetterInputsSchema,
    context: mandateLetterContextSchema,
    imSignatory: z.string(),
});
export type MandateLetterGetResponse = z.infer<typeof mandateLetterGetResponseSchema>;

export const mandateLetterContract = c.router(
    {
        getMandateLetter: {
            method: 'GET',
            path: '/desk/mandate/:clientId',
            pathParams: z.object({ clientId: z.string() }),
            responses: {
                200: mandateLetterGetResponseSchema,
                403: errorBody,
                404: errorBody,
            },
            summary: "A client's Discretionary Investment Mandate (or a prefilled blank)",
        },
        saveMandateLetter: {
            method: 'POST',
            path: '/desk/mandate/save',
            body: z.object({
                id: z.string().optional(),
                clientId: z.string().min(1),
                inputs: mandateLetterInputsSchema.partial(),
            }),
            responses: {
                200: z.object({ mandate: mandateLetterDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Create or patch the working draft of a mandate',
        },
        issueMandateLetter: {
            method: 'POST',
            path: '/desk/mandate/:id/issue',
            pathParams: z.object({ id: z.string() }),
            body: z.object({}).optional(),
            responses: {
                200: z.object({ mandate: mandateLetterDtoSchema }),
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
                404: staffActionErrorSchema,
            },
            summary: 'Mark a mandate as issued (draft → issued)',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
